<template>
  <div class="dropdown-parent-container">
    <button
      type="button"
      class="dropdown-menu-toggle-arrow-icon-button sc3-button sc3-button-grey sc3-button-control-bar-lower-size"
      :class="{ 'dropdown-menu-toggle-arrow-icon-button-open sc3-button-active': isOpen }"
      ref="moreDropdownMenuToggleButtonNode"
      @click="toggleDropdown()"
    >
      More
      &nbsp;
      <i class="fas fa-angle-down"></i>
    </button>
    <DropdownMenu
      :nodes-excluded-from-closing-on-outside-click="moreDropdownMenuToggleButtonNode ? [moreDropdownMenuToggleButtonNode] : []"
      :dropdown-button-left-value="left"
      v-model:is-open="isOpen"
      v-model:max-body-height="dropdownHeight"
      :dropdown-menu-toggle-button-node="moreDropdownMenuToggleButtonNode"
    >
      <template v-slot:body>
        <ul>
          <li :class="{ 'disabled': chartStore.chartType === CHART_TYPE_OPTION_PERF_CHART.value }">
            <a :href="classicSharpChartsLink" :disabled="chartStore.chartType === CHART_TYPE_OPTION_PERF_CHART.value" @click="closeDropdown()">
              <i class="custom-scc-icon icon-scc-pos-sq-sharp"></i>&nbsp;Classic SharpCharts
            </a>
          </li>
          <li class="dropdown-divider"></li>
          <li>
            <a :href="`/freecharts/symbolsummary.html?sym=${chartStore.urlEncodedSymbol}`" target="_blank" @click="closeDropdown()">
              <i class="far fa-fw fa-file-invoice-dollar"></i>
              &nbsp;Symbol Summary
            </a>
          </li>
          <li>
            <a :href="`/acp/?s=${chartStore.urlEncodedSymbol}`" target="_blank" @click="closeDropdown()">
              <i class="custom-scc-icon icon-scc-pos-sq-acp"></i>
              &nbsp;ACP
            </a>
          </li>
          <li>
            <a :href="`/freecharts/pnf.php?c=${chartStore.urlEncodedSymbol},P`" target="_blank" @click="closeDropdown()">
              <i class="custom-scc-icon icon-scc-pos-sq-pnf"></i>
              &nbsp;Point &amp; Figure
            </a>
          </li>
          <li>
            <a :href="`/freecharts/gallery.html?${chartStore.urlEncodedSymbol}`" target="_blank" @click="closeDropdown()">
              <i class="custom-scc-icon icon-scc-pos-sq-gallery"></i>
              &nbsp;GalleryView
            </a>
          </li>
          <li>
            <a :href="`/freecharts/seasonality.php?symbol=${chartStore.urlEncodedSymbol}`" target="_blank" @click="closeDropdown()">
              <i class="custom-scc-icon icon-scc-pos-sq-season"></i>
              &nbsp;Seasonality
            </a>
          </li>
          <li>
            <a :href="`/freecharts/options?sym=${chartStore.urlEncodedSymbol}`" target="_blank" @click="closeDropdown()">
              <i class="fas fa-fw fa-game-board-alt"></i>
              &nbsp;Options
            </a>
          </li>
          <li class="dropdown-divider"></li>
          <li>
            <a :href="interactivePerfChartLink" target="_blank" @click="closeDropdown()">
              <i class="custom-scc-icon icon-scc-pos-sq-perfchart"></i>
              &nbsp;Interactive PerfChart
            </a>
          </li>
          <li>
            <a :href="candleGlanceLink" target="_blank" @click="closeDropdown()">
              <i class="custom-scc-icon icon-scc-pos-sq-candle"></i>
              &nbsp;CandleGlance
            </a>
          </li>
          <li>
            <a :href="`/freecharts/rrg/?b=$SPX&p=w&y=1&t=10&f=tail,d&s=${chartStore.urlEncodedSymbol}`" target="_blank" @click="closeDropdown()">
              <i class="custom-scc-icon icon-scc-pos-sq-rrg"></i>
              &nbsp;RRG
            </a>
          </li>
        </ul>
      </template>
    </DropdownMenu>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed, watch } from 'vue';
  import { useChartStore } from '@/stores/chartStore';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useWorkbenchSelections } from '@/composables/useWorkbenchSelections';
  import { usePerfChartSymbols } from '@/composables/usePerfChartSymbols';
  import { useBoundingClientRect } from '@/composables/useBoundingClientRect';
  import { isValidChartRecid } from '@/composables/isValidChartRecid';
  import { isValidChartListListNum } from '@/composables/isValidChartListListNum';
  import DropdownMenu from '@/components/dropdowns/DropdownMenu.vue';
  import { CHART_TYPE_OPTION_PERF_CHART } from '@/enums/chart-type-options';

  const chartStore = useChartStore();
  const layoutStore = useLayoutStore();
  const { chartBeingViewed } = useWorkbenchSelections();
  const { getPerfChartSymbolsString } = usePerfChartSymbols();
  const { getBoundingClientRectPropertyRefs } = useBoundingClientRect();
  const moreDropdownMenuToggleButtonNode = ref<HTMLButtonElement>();
  const isOpen = ref<boolean>(false);
  const dropdownHeight = ref<number>(0);
  const { left } = getBoundingClientRectPropertyRefs(moreDropdownMenuToggleButtonNode, { windowResize: true });

  const candleGlanceLink = computed(() => {
    const perfChartSymbols = chartStore.chartType === CHART_TYPE_OPTION_PERF_CHART.value ? chartStore.perfChartSymbols : [];
    const symbols = getPerfChartSymbolsString(perfChartSymbols, chartStore.symbol);

    return `/freecharts/candleglance.html?${encodeURIComponent(symbols)}`;
  });

  const interactivePerfChartLink = computed(() => {
    const perfChartSymbols = chartStore.chartType === CHART_TYPE_OPTION_PERF_CHART.value ? chartStore.perfChartSymbols : [];
    const symbols = getPerfChartSymbolsString(perfChartSymbols, chartStore.symbol);

    return `/freecharts/perf.html?${encodeURIComponent(symbols)}`;
  });

  const classicSharpChartsLink = computed(() => {
    const initialParams = new URLSearchParams({
      s: chartStore.symbol
    });

    if (layoutStore.i) {
      initialParams.set('id', layoutStore.i);
    }

    if (isValidChartRecid(chartStore.recidOfChartBeingViewed)) {
      initialParams.set('a', chartStore.recidOfChartBeingViewed.toString());
    }

    if (chartBeingViewed.value && isValidChartListListNum(chartBeingViewed.value.chartListListNum)) {
      initialParams.set('listNum', chartBeingViewed.value.chartListListNum.toString());
    }

    return `/h-sc/ui?${initialParams.toString()}`;
  });

  function toggleDropdown() {
    isOpen.value = !isOpen.value;
  }

  function closeDropdown() {
    isOpen.value = false;
  }

  watch(isOpen, () => {
    if (!isOpen.value) {
      moreDropdownMenuToggleButtonNode.value?.blur();
    }
  });
</script>

<style scoped>
  .dropdown-parent-container {
    position: relative;
  }

  ul {
    max-height: v-bind('`${dropdownHeight}px`');
  }
</style>
